<script setup>
import { ref, computed, useId } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({ error: { type: String, default: '' } });
const emit = defineEmits(['import']);
const { t } = useI18n();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isImportingContact = computed(() => uiFlags.value.isImporting);

const dialogRef = ref(null);
const fileInput = ref(null);
const fileInputId = useId();

const hasSelectedFile = ref(null);
const selectedFileName = ref('');

const csvUrl = '/downloads/import-contacts-sample.csv';

const handleFileClick = () => {
  if (!isImportingContact.value) fileInput.value?.click();
};

const handleFileChange = () => {
  if (isImportingContact.value) return;
  const file = fileInput.value?.files[0];
  if (!file) return;
  hasSelectedFile.value = file;
  selectedFileName.value = file.name;
};

const handleRemoveFile = () => {
  if (isImportingContact.value) return;
  hasSelectedFile.value = null;
  if (fileInput.value) {
    fileInput.value.value = null;
  }
  selectedFileName.value = '';
};

const uploadFile = async () => {
  if (!hasSelectedFile.value || isImportingContact.value) return;
  emit('import', hasSelectedFile.value);
};

const requestClose = () => {
  if (!isImportingContact.value) dialogRef.value?.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TITLE')"
    :confirm-button-label="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.IMPORT')
    "
    :is-loading="isImportingContact"
    :disable-confirm-button="isImportingContact || !hasSelectedFile"
    :show-cancel-button="!isImportingContact"
    confirm-before-close
    @request-close="requestClose"
    @close="handleRemoveFile"
    @confirm="uploadFile"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DESCRIPTION') }}
        <a
          :href="csvUrl"
          target="_blank"
          rel="noopener noreferrer"
          download="import-contacts-sample.csv"
          class="text-n-blue-11"
        >
          {{
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DOWNLOAD_LABEL')
          }}
        </a>
      </p>
    </template>

    <div class="flex flex-col gap-2 [&_button]:!min-h-11 [&_button]:!rounded-xl">
      <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
        {{ error }}
      </p>
      <div class="flex flex-col gap-2">
        <label
          :for="fileInputId"
          class="text-sm text-n-slate-12 whitespace-nowrap"
        >
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABEL') }}
        </label>
        <div
          class="flex w-full min-w-0 flex-wrap items-center justify-between gap-2"
        >
          <span
            v-if="hasSelectedFile"
            class="min-w-0 break-all text-sm text-n-slate-12"
          >
            {{ selectedFileName }}
          </span>
          <Button
            v-if="!hasSelectedFile"
            :label="
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHOOSE_FILE')
            "
            icon="i-ph-upload-simple"
            color="slate"
            variant="ghost"
            :disabled="isImportingContact"
            size="sm"
            class="!w-fit"
            @click="handleFileClick"
          />
          <div v-else class="flex items-center gap-1">
            <Button
              :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHANGE')"
              color="slate"
              variant="ghost"
              :disabled="isImportingContact"
              size="sm"
              @click="handleFileClick"
            />
            <div class="w-px h-3 bg-n-strong" />
            <Button
              icon="i-ph-trash"
              :aria-label="
                t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.REMOVE')
              "
              color="slate"
              variant="ghost"
              :disabled="isImportingContact"
              size="sm"
              @click="handleRemoveFile"
            />
          </div>
        </div>
      </div>
    </div>
    <input
      :id="fileInputId"
      ref="fileInput"
      :disabled="isImportingContact"
      type="file"
      accept="text/csv"
      class="hidden"
      @change="handleFileChange"
    />
  </Dialog>
</template>
